/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { NativeModules } from 'react-native';
import {
  useFlagEnabled,
  useIsLoggedIn,
  useSetting,
  useUser,
} from '@airasia-rn/account';
import { LoggerProviderType, useLogger } from '@airasia-rn/core-logger';
import { HomepageEvent, HomepageMeta } from '../../analytics';
import {
  FEATURE_FLAG_ENABLE_MOVEMENTS_SOCIAL_GRID,
  FEATURE_FLAG_MOVEMENTS_AGE_RESTRICTION_ENABLED,
  MINIMUM_ALLOWED_AGE_YEARS,
} from '../../constants';

const SKELETON_MIN_VISIBLE_MS = 450;
const SKELETON_FALLBACK_HIDE_DELAY_MS = 5000;

// Module-level flag preserves the original one-time skeleton behavior across
// HomePage remounts within the same app session.
let homepageHasLoaded = false;

// Shared runtime hooks keep cross-platform side effects in one place while the
// page files stay focused on rendering their native or web list composition.
export const useMovementsV4LayoutEnabled = () => {
  const [isV4LayoutEnabled, setIsV4LayoutEnabled] = useState(false);

  useEffect(() => {
    NativeModules.NativeRemoteConfig?.getValueForKeys?.(
      ['home_movements_v4_layout_enabled'],
      (config: { home_movements_v4_layout_enabled?: string | boolean }) => {
        if (config?.home_movements_v4_layout_enabled !== undefined) {
          const val = String(
            config.home_movements_v4_layout_enabled,
          ).toLowerCase();
          setIsV4LayoutEnabled(val === 'true' || val === '1');
        }
      },
    );
  }, []);

  return isV4LayoutEnabled;
};

const birthYearFromDob = (dateOfBirth?: string | null): number | null => {
  const value = dateOfBirth?.trim() ?? '';
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  const compact = /^(\d{2})(\d{2})(\d{4})$/.exec(value);
  const slashed = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(value);
  const year = Number(iso?.[1] ?? compact?.[3] ?? slashed?.[3]);
  const month = Number(iso?.[2] ?? compact?.[2] ?? slashed?.[2]);
  const day = Number(iso?.[3] ?? compact?.[1] ?? slashed?.[1]);
  if (!iso && !compact && !slashed) return null;

  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return year;
};

export type MovementsAgeEligibility = 'pending' | 'eligible' | 'ineligible';

export const useProfileMovementsAgeEligible = (): MovementsAgeEligibility => {
  const {
    enabled: isAgeRestrictionEnabled,
    isSettingsLoading: isAgeFlagLoading,
  } = useFlagEnabled(FEATURE_FLAG_MOVEMENTS_AGE_RESTRICTION_ENABLED);
  const { value: minimumAgeValue, isSettingsLoading: isMinimumAgeLoading } =
    useSetting(MINIMUM_ALLOWED_AGE_YEARS) || {};
  const userQuery = useUser('useMovementsEnabled');
  const loginQuery = useIsLoggedIn('useMovementsEnabled');

  // Flag defaults to off while settings load, which would look eligible.
  if (isAgeFlagLoading || isMinimumAgeLoading) return 'pending';
  if (!isAgeRestrictionEnabled) return 'eligible';

  // A logged-out session leaves the profile query disabled, which also looks
  // pending. Resolve login before treating a quiet profile query as loading.
  if (loginQuery.isPending || loginQuery.isLoading) return 'pending';
  if (!loginQuery.isLoggedIn) return 'ineligible';

  if (userQuery.isError) return 'ineligible';
  if (!userQuery.isSuccess) return 'pending';

  const birthYear = birthYearFromDob(userQuery.data?.dateOfBirth);
  if (birthYear == null) return 'ineligible';

  const parsedAge = Number(minimumAgeValue);
  const minimumAllowedAge =
    Number.isInteger(parsedAge) && parsedAge > 0 ? parsedAge : 17;
  return new Date().getFullYear() - birthYear >= minimumAllowedAge
    ? 'eligible'
    : 'ineligible';
};

export const useMovementsEnabled = (hostAgeEligible = true) => {
  const isAgeEligible = useProfileMovementsAgeEligible() === 'eligible';
  const { enabled: isSocialGridEnabled } = useFlagEnabled(
    FEATURE_FLAG_ENABLE_MOVEMENTS_SOCIAL_GRID,
  );
  const { enabled: isMovementsFlagEnabled } =
    useFlagEnabled('enable_movements');
  const [isMovementsRemoteConfigEnabled, setIsMovementsRemoteConfigEnabled] =
    useState(false);

  useEffect(() => {
    if (!isMovementsFlagEnabled) {
      setIsMovementsRemoteConfigEnabled(false);
      return;
    }

    NativeModules.NativeRemoteConfig?.getValueForKeys?.(
      ['enable_movements'],
      (config: { enable_movements?: string | boolean }) => {
        if (config?.enable_movements !== undefined) {
          setIsMovementsRemoteConfigEnabled(
            String(config.enable_movements).toLowerCase() === 'true',
          );
        }
      },
    );
  }, [isMovementsFlagEnabled]);

  return (
    isMovementsRemoteConfigEnabled &&
    isMovementsFlagEnabled &&
    isSocialGridEnabled &&
    hostAgeEligible &&
    isAgeEligible
  );
};

let hasHomeRendered = false;

export const getHasHomeRendered = () => hasHomeRendered;

export const useReactNativeReadyNotification = () => {
  useEffect(() => {
    hasHomeRendered = true;
    NativeModules.ReactToNative?.notifyRNReady?.();
  }, []);
};

export const useHomePageAnalytics = ({
  error,
  onLoginMissing,
  trackScreenload = true,
}: {
  error: unknown;
  onLoginMissing?: () => void;
  trackScreenload?: boolean;
}) => {
  const { logEvent, logError } = useLogger();
  const hasTrackedScreenload = useRef(false);
  const hasTrackedCosmosError = useRef(false);

  useEffect(() => {
    if (!trackScreenload) return;
    if (hasTrackedScreenload.current) return;

    hasTrackedScreenload.current = true;
    const eventName = HomepageMeta.EVENT_NAMES.AA_ALL_APP_SCREENLOAD;
    const eventData = HomepageEvent.homepageScreenViewEvent();
    [LoggerProviderType.moengage, LoggerProviderType.conviva].forEach(
      (provider) => logEvent(eventName, eventData, provider),
    );
  }, [logEvent, trackScreenload]);

  useEffect(() => {
    if (!error || hasTrackedCosmosError.current) return;

    hasTrackedCosmosError.current = true;
    logError(
      HomepageMeta.EVENT_NAMES.API_ERROR,
      HomepageEvent.apiErrorEvent({
        error,
        functionality: 'Homepage User Intent',
        label: 'home_page_generic_error_message|null',
        category: 'Homepage|Cosmos',
      }),
      LoggerProviderType.all,
    );
  }, [error, logError]);

  useEffect(() => {
    const errorMessage =
      error instanceof Error
        ? error.message
        : typeof error === 'object' && error !== null && 'message' in error
          ? String((error as { message?: unknown }).message ?? '')
          : '';

    if (errorMessage.includes('No refresh token found')) onLoginMissing?.();
  }, [error, onLoginMissing]);
};

export const useHomePageSkeleton = ({
  isLoading,
  isRefreshing,
  listItemCount,
}: {
  isLoading: boolean;
  isRefreshing: boolean;
  listItemCount: number;
}) => {
  const [showSkeleton, setShowSkeleton] = useState(!homepageHasLoaded);
  const [hasListRendered, setHasListRendered] = useState(false);
  const hasObservedInitialLoadingRef = useRef(false);
  const skeletonFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const skeletonHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const skeletonShownAtRef = useRef<number | null>(
    !homepageHasLoaded ? Date.now() : null,
  );

  useEffect(() => {
    if (isLoading || isRefreshing) hasObservedInitialLoadingRef.current = true;
  }, [isLoading, isRefreshing]);

  useEffect(() => {
    if (homepageHasLoaded) return;

    if (hasListRendered) {
      const shownAt = skeletonShownAtRef.current;
      const elapsedMs = shownAt ? Date.now() - shownAt : 0;
      skeletonHideTimerRef.current = setTimeout(
        () => {
          homepageHasLoaded = true;
          setShowSkeleton(false);
        },
        Math.max(0, SKELETON_MIN_VISIBLE_MS - elapsedMs),
      );
      return;
    }

    skeletonFallbackTimerRef.current = setTimeout(() => {
      homepageHasLoaded = true;
      setShowSkeleton(false);
    }, SKELETON_FALLBACK_HIDE_DELAY_MS);

    return () => {
      if (skeletonFallbackTimerRef.current) {
        clearTimeout(skeletonFallbackTimerRef.current);
        skeletonFallbackTimerRef.current = null;
      }
      if (skeletonHideTimerRef.current) {
        clearTimeout(skeletonHideTimerRef.current);
        skeletonHideTimerRef.current = null;
      }
    };
  }, [hasListRendered]);

  const handleListLayout = useCallback(() => setHasListRendered(true), []);
  const canRenderList =
    listItemCount > 0 ||
    (hasObservedInitialLoadingRef.current && !isLoading && !isRefreshing);

  return {
    canRenderList,
    handleListLayout,
    showSkeleton,
  };
};
